// Community v1.1.0 — API helpers, server-clock session hook and private drafts.
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from './auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/community`;
const cfg = { withCredentials: true };

let _configPromise = null;
export function loadConfig() {
  if (!_configPromise) _configPromise = axios.get(`${API}/config`, cfg).then((r) => r.data).catch((e) => { _configPromise = null; throw e; });
  return _configPromise;
}

export const api = {
  session: () => axios.get(`${API}/session`, cfg).then((r) => r.data),
  list: (params) => axios.get(`${API}/questions`, { ...cfg, params }).then((r) => r.data),
  question: (id) => axios.get(`${API}/questions/${id}`, cfg).then((r) => r.data),
  mine: () => axios.get(`${API}/me/contributions`, cfg).then((r) => r.data),
  ask: (body, key) => axios.post(`${API}/questions`, body, { ...cfg, headers: { 'Idempotency-Key': key } }).then((r) => r.data),
  answer: (qid, body, key) => axios.post(`${API}/questions/${qid}/answers`, body, { ...cfg, headers: { 'Idempotency-Key': key } }).then((r) => r.data),
  editQuestion: (id, body) => axios.patch(`${API}/questions/${id}`, body, cfg).then((r) => r.data),
  editAnswer: (id, body) => axios.patch(`${API}/answers/${id}`, body, cfg).then((r) => r.data),
  removeQuestion: (id) => axios.delete(`${API}/questions/${id}`, cfg).then((r) => r.data),
  removeAnswer: (id) => axios.delete(`${API}/answers/${id}`, cfg).then((r) => r.data),
  helpful: (qid, answer_id) => axios.post(`${API}/questions/${qid}/helpful`, { answer_id }, cfg).then((r) => r.data),
  report: (body) => axios.post(`${API}/reports`, body, cfg).then((r) => r.data),
  adminQueue: () => axios.get(`${API}/admin/queue`, cfg).then((r) => r.data),
  moderate: (type, id, body) => axios.post(`${API}/admin/${type}/${id}/moderate`, body, cfg).then((r) => r.data),
  suspend: (body) => axios.post(`${API}/admin/suspend`, body, cfg).then((r) => r.data),
  settings: (body) => axios.patch(`${API}/admin/settings`, body, cfg).then((r) => r.data),
  testClock: (offset_seconds) => axios.post(`${API}/admin/test-clock`, { offset_seconds }, cfg).then((r) => r.data),
};

export const newKey = () => (window.crypto?.randomUUID?.() || `k_${Date.now()}_${Math.random().toString(36).slice(2)}`);

export function errorInfo(err) {
  const status = err?.response?.status;
  const d = err?.response?.data?.detail;
  const code = d && typeof d === 'object' ? d.code : null;
  let message = typeof d === 'string' ? d : d?.message || 'Something went wrong. Please try again.';
  if (Array.isArray(d)) message = d.map((e) => e.msg).join(' ');
  if (code === 'quota') {
    const t = d.type === 'question' ? 'question' : 'answer';
    message = d.scope === 'device'
      ? `This browser/device has reached today's ${t} allowance. People sharing this browser share its allowance. Resets at midnight IST.`
      : `You have used today's ${t} allowance. You can ${t === 'question' ? 'ask' : 'answer'} again after midnight IST.`;
  }
  return { status, code, message };
}

// IST helpers (display only — the server clock is authoritative).
export const fmtIST = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', timeZone: 'Asia/Kolkata' }) + ' IST';
};
export const untilLabel = (resetsAt, serverTime) => {
  if (!resetsAt) return '';
  const ms = new Date(resetsAt) - (serverTime ? new Date(serverTime) : new Date());
  if (ms <= 0) return 'now';
  const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const DAY_EVENT = 'lxt-community-day';
export const onDayChange = (fn) => { window.addEventListener(DAY_EVENT, fn); return () => window.removeEventListener(DAY_EVENT, fn); };

/** Server-clock aware allowance session. Refreshes at the IST midnight boundary and on focus/pageshow/online. */
export function useCommunitySession() {
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState('');
  const dayRef = useRef(null);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const s = await api.session();
      setError('');
      setSession(s);
      if (dayRef.current && dayRef.current !== s.community_day_key) window.dispatchEvent(new CustomEvent(DAY_EVENT, { detail: s }));
      dayRef.current = s.community_day_key;
      clearTimeout(timerRef.current);
      const ms = Math.max(1000, new Date(s.resets_at) - new Date(s.server_time) + 1500);
      timerRef.current = setTimeout(refresh, Math.min(ms, 2147000000));
    } catch (e) {
      if (e?.response?.status !== 401) setError('Connect to refresh today\'s community and check your remaining allowance.');
    }
  }, []);

  useEffect(() => { loadConfig().then(setConfig).catch(() => setError('We could not load the community. Please try again.')); }, []);
  useEffect(() => {
    if (!user) return undefined;
    refresh();
    const onWake = () => { if (document.visibilityState !== 'hidden') refresh(); };
    window.addEventListener('focus', onWake);
    window.addEventListener('pageshow', onWake);
    window.addEventListener('online', onWake);
    document.addEventListener('visibilitychange', onWake);
    return () => {
      clearTimeout(timerRef.current);
      window.removeEventListener('focus', onWake);
      window.removeEventListener('pageshow', onWake);
      window.removeEventListener('online', onWake);
      document.removeEventListener('visibilitychange', onWake);
    };
  }, [user, refresh]);

  return { session, config, error, refresh, user };
}

// Private drafts — per account + context, never sent automatically.
const draftKey = (uid, ctx) => `lxt_cdraft:${uid || 'anon'}:${ctx}`;
export const loadDraft = (uid, ctx) => { try { return JSON.parse(localStorage.getItem(draftKey(uid, ctx)) || 'null'); } catch { return null; } };
export const saveDraft = (uid, ctx, v) => { try { localStorage.setItem(draftKey(uid, ctx), JSON.stringify(v)); } catch { /* ignore */ } };
export const clearDraft = (uid, ctx) => { try { localStorage.removeItem(draftKey(uid, ctx)); } catch { /* ignore */ } };
